import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  MessageCircle,
} from "lucide-react";

const Paystack = () => {
  const location = useLocation();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Verifying payment...");

  const whatsappNumber = "2348069804062";

  // WhatsApp success message
  const successWhatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Doctor, I have completed my subscription payment and would like to continue my consultation.`
    );
  }, []);

  // WhatsApp failed message
  const failedWhatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Zaviramed, I need help with my subscription payment.`
    );
  }, []);

  useEffect(() => {
    const handleCallback = async () => {
      const query = new URLSearchParams(location.search);

      try {
        const reference = query.get("reference");
        const clientId = query.get("clientId");

        if (!reference) {
          setStatus("error");
          setMessage("Invalid payment reference.");
          return;
        }

        const res = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "/api/paystack/callback",
          {
            reference,
            clientId,
          }
        );

        console.log(res.data);

        if (res.data.success) {
          setStatus("success");
          setMessage(
            "Payment successful. Your subscription has been activated."
          );
        } else {
          setStatus("error");
          setMessage(
            res.data.message || "Payment verification failed."
          );
        }
      } catch (err) {
        console.error("Callback error:", err);
        setStatus("error");
        setMessage(
          "We could not verify your payment. Please contact support on WhatsApp."
        );
      }
    };

    handleCallback();
  }, [location.search]);

  const openSuccessWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${successWhatsappText}`,
      "_blank"
    );
  };

  const openFailedWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${failedWhatsappText}`,
      "_blank"
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#F7F8FD] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white border border-gray-100 shadow-xl rounded-3xl p-8 text-center">

        {/* Status Icon */}
        <div className="flex justify-center mb-5">
          {status === "loading" && (
            <div className="h-16 w-16 rounded-full bg-[#25DAC5]/10 flex items-center justify-center">
              <Loader2 className="animate-spin text-[#25DAC5]" size={30} />
            </div>
          )}

          {status === "success" && (
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-600" size={32} />
            </div>
          )}

          {status === "error" && (
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="text-red-600" size={32} />
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-[#1E0E62] mb-3">
          Subscription Payment
        </h1>

        {/* Message */}
        <p className="text-gray-600 leading-7 mb-6">{message}</p>

        {/* Success Button */}
        {status === "success" && (
          <button
            onClick={openSuccessWhatsapp}
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-3 transition"
          >
            <MessageCircle size={18} />
            Open WhatsApp
          </button>
        )}

        {/* Error Button */}
        {status === "error" && (
          <button
            onClick={openFailedWhatsapp}
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] hover:opacity-90 text-white font-semibold px-5 py-3 transition"
          >
            <MessageCircle size={18} />
            Contact Support on WhatsApp
          </button>
        )}

        {/* Loading Text */}
        {status === "loading" && (
          <div className="text-sm text-gray-400">
            Please wait while we confirm your payment.
          </div>
        )}
      </div>
    </div>
  );
};

export default Paystack;